import Swal from "sweetalert2/dist/sweetalert2.js";

const TOAST_ICON_BY_METHOD = {
  s: "success",
  w: "warning",
  e: "error",
  i: "info"
};

const ICON_BY_TYPE = {
  dark: "question",
  red: "error",
  orange: "warning",
  green: "success",
  blue: "info"
};

function sanitizeCashbackText(value) {
  if (typeof value !== "string") {
    return value || "";
  }

  return value
    .replace(/cr[e�]dito\s*\/\s*cash\s*-?\s*back/gi, "credito")
    .replace(/cash\s*-?\s*back/gi, "credito");
}

const toast = Swal.mixin({
  toast: true,
  position: "top-end",
  timer: 4200,
  timerProgressBar: true,
  showConfirmButton: false,
  customClass: {
    popup: "dracosis-toast-popup",
    title: "dracosis-toast-title",
    icon: "dracosis-toast-icon"
  },
  zIndex: 2000
});

function stripTags(value) {
  if (typeof value !== "string") {
    return value || "";
  }

  return sanitizeCashbackText(value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim());
}

function normalizeButton(buttonKey, rawButton, index) {
  const fallbackText = buttonKey || "ok";

  if (typeof rawButton === "function") {
    return {
      key: fallbackText,
      text: fallbackText,
      action: rawButton,
      keys: [],
      btnClass: "",
      index
    };
  }

  const buttonConfig = rawButton || {};
  return {
    key: fallbackText,
    text: stripTags(buttonConfig.text || fallbackText),
    action: buttonConfig.action,
    keys: Array.isArray(buttonConfig.keys) ? buttonConfig.keys : [],
    btnClass: buttonConfig.btnClass || "",
    index
  };
}

function normalizeButtons(buttonsConfig) {
  if (!buttonsConfig || typeof buttonsConfig !== "object") {
    return [
      {
        key: "ok",
        text: "Ok",
        action: null,
        keys: ["Enter"],
        btnClass: "btn-blue",
        index: 0
      }
    ];
  }

  const buttonKeys = Object.keys(buttonsConfig);
  if (buttonKeys.length === 0) {
    return [
      {
        key: "ok",
        text: "Ok",
        action: null,
        keys: ["Enter"],
        btnClass: "btn-blue",
        index: 0
      }
    ];
  }

  return buttonKeys.map((buttonKey, index) => normalizeButton(buttonKey, buttonsConfig[buttonKey], index));
}

function getButtonVariantClass(button) {
  const btnClass = String(button.btnClass || "").toLowerCase();

  if (btnClass.indexOf("danger") >= 0 || btnClass.indexOf("red") >= 0) {
    return "dracosis-confirm-btn--danger";
  }

  if (btnClass.indexOf("success") >= 0 || btnClass.indexOf("green") >= 0) {
    return "dracosis-confirm-btn--success";
  }

  if (btnClass.indexOf("secondary") >= 0 || btnClass.indexOf("default") >= 0 || button.key === "nao" || button.key === "cancelar") {
    return "dracosis-confirm-btn--neutral";
  }

  if (button.index === 0) {
    return "dracosis-confirm-btn--primary";
  }

  return "dracosis-confirm-btn--neutral";
}

function getDialogIcon(options) {
  if (options && options.icon) {
    const iconText = String(options.icon).toLowerCase();
    if (iconText.indexOf("question") >= 0) {
      return "question";
    }
    if (iconText.indexOf("error") >= 0 || iconText.indexOf("times") >= 0) {
      return "error";
    }
    if (iconText.indexOf("warning") >= 0 || iconText.indexOf("exclamation") >= 0) {
      return "warning";
    }
    if (iconText.indexOf("check") >= 0 || iconText.indexOf("success") >= 0) {
      return "success";
    }
  }

  const typeValue = options && options.type ? String(options.type).toLowerCase() : "";
  return ICON_BY_TYPE[typeValue] || "question";
}

function buildDialogHtml(content, buttons) {
  const message = sanitizeCashbackText(content || "");
  const buttonsMarkup = buttons.map((button) => {
    const variantClass = getButtonVariantClass(button);
    return `<button type="button" data-dracosis-btn="${button.key}" class="swal2-styled dracosis-confirm-btn ${variantClass}">${button.text}</button>`;
  }).join("");

  return `<div class="dracosis-confirm-message">${message}</div><div class="dracosis-confirm-actions">${buttonsMarkup}</div>`;
}

function runButtonAction(button, actionContext) {
  if (typeof button.action === "function") {
    try {
      button.action.call(actionContext);
    } catch (error) {
      // Keep current behavior non-blocking and visible in dev tools.
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}

function createConfirm(options) {
  const normalizedOptions = typeof options === "string" ? { content: options } : (options || {});
  const buttons = normalizeButtons(normalizedOptions.buttons);
  const escapeKeyButton = typeof normalizedOptions.escapeKey === "string" ? normalizedOptions.escapeKey : null;
  const keyToButton = {};

  buttons.forEach((button) => {
    button.keys.forEach((shortcut) => {
      keyToButton[String(shortcut).toLowerCase()] = button.key;
    });
  });

  return Swal.fire({
    title: sanitizeCashbackText(normalizedOptions.title || "Confirmacao"),
    html: buildDialogHtml(normalizedOptions.content, buttons),
    icon: getDialogIcon(normalizedOptions),
    showConfirmButton: false,
    showCancelButton: false,
    allowOutsideClick: normalizedOptions.backgroundDismiss !== false,
    allowEscapeKey: escapeKeyButton ? false : normalizedOptions.escapeKey !== false,
    customClass: {
      popup: "dracosis-confirm-popup",
      title: "dracosis-confirm-title"
    },
    didOpen: (popup) => {
      const actionContext = { buttons: {} };

      buttons.forEach((button) => {
        actionContext.buttons[button.key] = {
          disable: () => {},
          enable: () => {}
        };
      });

      buttons.forEach((button) => {
        const element = popup.querySelector(`[data-dracosis-btn="${button.key}"]`);
        if (!element) {
          return;
        }

        actionContext.buttons[button.key] = {
          disable: () => {
            element.disabled = true;
          },
          enable: () => {
            element.disabled = false;
          }
        };

        element.addEventListener("click", () => {
          Swal.close();
          runButtonAction(button, actionContext);
        });
      });

      popup.addEventListener("keydown", (event) => {
        const keyValue = String(event.key || "").toLowerCase();

        if (event.key === "Escape" && escapeKeyButton) {
          event.preventDefault();
          const escapeButtonElement = popup.querySelector(`[data-dracosis-btn="${escapeKeyButton}"]`);
          if (escapeButtonElement) {
            escapeButtonElement.click();
          } else {
            Swal.close();
          }
          return;
        }

        if (keyToButton[keyValue]) {
          event.preventDefault();
          const mappedButtonElement = popup.querySelector(`[data-dracosis-btn="${keyToButton[keyValue]}"]`);
          if (mappedButtonElement) {
            mappedButtonElement.click();
          }
        }
      });

      const firstButton = popup.querySelector("[data-dracosis-btn]");
      if (firstButton) {
        firstButton.focus();
      }
    }
  });
}

function fireToast(method, message, title) {
  const icon = TOAST_ICON_BY_METHOD[method] || "info";
  return toast.fire({
    icon,
    title: sanitizeCashbackText(message),
    text: sanitizeCashbackText(title || "")
  });
}

function createToastrApi() {
  return {
    s(message, title) {
      return fireToast("s", message, title);
    },
    w(message, title) {
      return fireToast("w", message, title);
    },
    e(message, title) {
      return fireToast("e", message, title);
    },
    i(message, title) {
      return fireToast("i", message, title);
    },
    remove() {
      Swal.close();
    }
  };
}

const SweetAlertBridge = {
  install(Vue) {
    const toastrApi = createToastrApi();

    Vue.prototype.$toastr = toastrApi;

    window.Toast = toast;

    if (window.$) {
      window.$.confirm = createConfirm;
    }

    if (window.jQuery) {
      window.jQuery.confirm = createConfirm;
    }
  }
};

export default SweetAlertBridge;
